import { FC, useCallback } from "react";
import { useToggle } from "rooks";
import { Card, Collapse, Stack } from "@mui/material";
import { LiveUsageResourceGrid } from "./LiveUsageResourceGrid";
import { LiveUsageResourceCardDialog } from "./dialog/LiveUsageResourceCardDialog";
import { LiveUsageResourceCardHeader } from "./header/LiveUsageResourceCardHeader";
import { useResourceCardCollapsedStateFromStorage } from "../../utils/hooks/useResourceCardState.hook";
import { ResourceType } from "@/services/cloudchipr.api";

interface LiveUsageResourceCardProps {
  singleView?: boolean;
  initialCollapsed?: boolean;
  resourceType: ResourceType;
}

export const LiveUsageResourceCard: FC<LiveUsageResourceCardProps> = ({
  resourceType,
  singleView,
  initialCollapsed,
}) => {
  const { collapsedState, setCollapsedState } =
    useResourceCardCollapsedStateFromStorage(resourceType, initialCollapsed);

  const [collapsed, toggleCollapsed] = useToggle(collapsedState);
  const [fullScreen, toggleFullScreen] = useToggle();

  const onCollapse = useCallback(() => {
    setCollapsedState(!collapsed);
    toggleCollapsed(!collapsed);
  }, [collapsed, setCollapsedState, toggleCollapsed]);

  if (singleView) {
    const masterGridId = `${resourceType}-single-view-master-grid-id`;

    return (
      <Stack width="100%">
        <LiveUsageResourceGrid
          masterGridId={masterGridId}
          resourceType={resourceType}
        />
      </Stack>
    );
  }
  const masterGridId = `${resourceType}-multi-view-master-grid-id`;

  return (
    <Card variant="outlined" sx={{ borderColor: "primary.light" }}>
      <LiveUsageResourceCardHeader
        resourceType={resourceType}
        onCollapse={onCollapse}
        onFullScreen={toggleFullScreen}
        collapsed={collapsed}
      />

      <Collapse in={!collapsed}>
        {!collapsed && (
          <LiveUsageResourceGrid
            allResourcesView
            resourceType={resourceType}
            masterGridId={masterGridId}
          />
        )}
      </Collapse>

      {fullScreen && (
        <LiveUsageResourceCardDialog
          resourceType={resourceType}
          onFullScreen={toggleFullScreen}
        />
      )}
    </Card>
  );
};
